/**
 * reCAPTCHA v2 checkbox — script loader.
 *
 * Componente React em `shared/components/RecaptchaCheckbox.tsx` consome este
 * helper. Carrega https://www.google.com/recaptcha/api.js uma unica vez via
 * `explicit` render mode (assim controlamos quando renderizar cada widget).
 *
 * Site key vem de VITE_RECAPTCHA_SITE_KEY. Vazio = modo dev (sem captcha).
 */

const SCRIPT_ID = 'recaptcha-v2-script';

export const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

let _loadPromise: Promise<void> | null = null;

/**
 * Garante que o script reCAPTCHA esta carregado e `window.grecaptcha.render` esta pronto.
 * Idempotente.
 */
export function ensureRecaptchaLoaded(): Promise<void> {
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve();
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise<void>((resolve, reject) => {
    if (window.grecaptcha?.render) return resolve();

    // Google chama essa fn assim que api.js termina de baixar
    window.onRecaptchaLoad = () => resolve();

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      // ja foi disparado outro load — espera o callback global
      const start = Date.now();
      const poll = setInterval(() => {
        if (window.grecaptcha?.render) {
          clearInterval(poll);
          resolve();
        } else if (Date.now() - start > 10000) {
          clearInterval(poll);
          reject(new Error('reCAPTCHA load timeout'));
        }
      }, 100);
      return;
    }

    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit&hl=pt-BR';
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('reCAPTCHA load failed'));
    document.head.appendChild(s);
  });
  return _loadPromise;
}

export const isRecaptchaEnabled = (): boolean => Boolean(RECAPTCHA_SITE_KEY);
